import React from "react";
import _orderBy from "lodash/orderBy";
import { useSelector } from "react-redux";
import Cookies from "js-cookie";
import { ArrowUpIcon, ArrowDownIcon } from "@heroicons/react/outline";

import ReactTable from "components/lib/Shared/ReactTable";
import TableActionMenu from "components/lib/Shared/TableActionMenu";
import { roundNumber } from "utils";
import { Constants } from "config/constants";

const CorporateShareholderTable = (props) => {
  const {
    corporateShareholders,
    handleEdit,
    isCorporateActive,
    handleDelete,
    allottedShares,
    allottedVotingRights,
    expectedTotalShares,
    expectedTotalVoting,
    hasCapitalChange,
    isInUpdateCompanyDetailsContext,
    parentInfo,
  } = props;

  const currentUser = useSelector((state) => state.userSlice);
  const role = Cookies.get("role");

  const transformRightLabel = (value) => {
    return value === "yes" ? "Yes" : "No";
  };

  /**
   * Get suggested percentage for a child based on parent info
   */
  const getSuggestedPercentage = (child) => {
    if (!parentInfo || !parentInfo.children) return null;

    const matchingChild = parentInfo.children.find((c) => c._id === child._id);
    return matchingChild?._suggestedPercentage;
  };

  const columns = [
    {
      Header: "Sn",
      Cell: ({ row }) => {
        if (row.original.id === "total") return <b>Total</b>;
        return row.index + 1;
      },
    },
    {
      Header: "Role",
      Cell: ({ row: { original } }) => {
        if (original.id === "total") return "";
        if (original.remainingMinorityShareholders) return "Shareholders";
        return original?.role?.join(", ") ?? "";
      },
    },
    {
      Header: "Full Name",
      className: "p-4 text-ellipsis overflow-hidden max-w-[150px]",
      Cell: ({ row: { original } }) => {
        if (original.id === "total") return "";
        if (original.remainingMinorityShareholders) {
          const minorityFullName = `${original.remainingMinorityShareholders} Minority Shareholders`;
          return <span>{minorityFullName}</span>;
        }
        const fullName = original.names?.fullName ?? "";
        return <span>{fullName}</span>;
      },
    },
    { Header: "ID Type", accessor: "idType" },
    {
      Header: "Percentage Shareholding",
      Cell: ({ row: { original } }) => {
        if (original.id === "total") {
          const diff = expectedTotalShares - allottedShares;
          const isMatch = Math.abs(diff) < 0.01;
          return (
            <div>
              <b className={!isMatch && hasCapitalChange ? "text-red-600" : ""}>
                {`${allottedShares}%`}
              </b>
              {hasCapitalChange && !isMatch && (
                <div className="text-xs text-gray-500 mt-1">
                  Target: {expectedTotalShares}%
                  {diff > 0 && (
                    <span className="text-blue-600 ml-1">
                      (+{diff.toFixed(2)}% needed)
                    </span>
                  )}
                  {diff < 0 && (
                    <span className="text-orange-600 ml-1">
                      ({Math.abs(diff).toFixed(2)}% excess)
                    </span>
                  )}
                </div>
              )}
            </div>
          );
        }

        const currentPercent = roundNumber(
          Number(
            original.beneficialOwnershipForm?.directPercentShareholding || 0
          ) +
            Number(
              original.beneficialOwnershipForm?.indirectPercentShareholding || 0
            )
        );

        const totalMinority =
          "totalMinorityShareholdingPercent" in original
            ? roundNumber(original.totalMinorityShareholdingPercent)
            : null;

        const suggested = getSuggestedPercentage(original);

        return (
          <div>
            <span>
              {totalMinority !== null
                ? `${totalMinority}%`
                : `${currentPercent}%`}
            </span>
            {suggested !== undefined && suggested !== null && (
              <div
                className={`text-xs mt-1 ${
                  parentInfo?.isDecrease ? "text-red-600" : "text-green-600"
                }`}
              >
                {parentInfo?.isDecrease ? (
                  <>
                    <ArrowDownIcon className="w-3 h-3 inline mr-1" />
                    Suggested: {suggested.toFixed(2)}%
                    <span className="ml-1">
                      (-{(currentPercent - suggested).toFixed(2)}%)
                    </span>
                  </>
                ) : (
                  <>
                    <ArrowUpIcon className="w-3 h-3 inline mr-1" />
                    Suggested: {suggested.toFixed(2)}%
                    <span className="ml-1">
                      (+{(suggested - currentPercent).toFixed(2)}%)
                    </span>
                  </>
                )}
              </div>
            )}
          </div>
        );
      },
    },
    {
      Header: "Percentage Voting",
      Cell: ({ row: { original } }) => {
        if (original.id === "total") {
          const diff = expectedTotalVoting - allottedVotingRights;
          const isMatch = Math.abs(diff) < 0.01;
          return (
            <div>
              <b className={!isMatch && hasCapitalChange ? "text-red-600" : ""}>
                {`${allottedVotingRights}%`}
              </b>
              {hasCapitalChange && !isMatch && (
                <div className="text-xs text-gray-500 mt-1">
                  Target: {expectedTotalVoting}%
                </div>
              )}
            </div>
          );
        }
        if ("totalMinorityVotingPercent" in original)
          return (
            <span>{`${roundNumber(
              original.totalMinorityVotingPercent
            )}%`}</span>
          );
        return `${roundNumber(
          Number(
            original.beneficialOwnershipForm?.directPercentVotingRights || 0
          ) +
            Number(
              original.beneficialOwnershipForm?.indirectPercentVotingRights || 0
            )
        )}%`;
      },
    },
    {
      Header: "Appoint/Remove Director",
      Cell: ({ row: { original } }) => {
        if (original.id === "total") return "";
        return transformRightLabel(
          original.beneficialOwnershipForm?.indirectRightRemoveDirector
        );
      },
    },
    {
      Header: "Significant control over company",
      Cell: ({ row: { original } }) => {
        if (original.id === "total") return "";
        return transformRightLabel(
          original.beneficialOwnershipForm?.indirectCompanyControlRight
        );
      },
    },
    {
      Header: isCorporateActive ? "" : "Action",
      Cell: ({ row }) => {
        if (row.original.id === "total") return "";

        const isOwnerRow = [
          row.original?.electronicAddress?.emailAddress,
          row.original?.identificationDetails?.emailAddress,
        ].includes(currentUser.electronicAddress.emailAddress);

        const disableEditBtn =
          !isInUpdateCompanyDetailsContext &&
          (((row.original?.type === "invited" ||
            role === Constants.COOKIE_ROLES.OFFICIAL) &&
            !isOwnerRow) ||
            row.original?.isReadOnly);

        // Hide actions entirely if corporate is active
        if (isCorporateActive) {
          return <div className="text-xs text-gray-500 italic"></div>;
        }

        return (
          <div className="flex items-center space-x-2">
            <TableActionMenu
              row={row}
              showViewBtn={role !== Constants.COOKIE_ROLES.OFFICIAL}
              showReviewBtn={
                !isOwnerRow &&
                row.original?.type === "invited" &&
                row.original.createdBy === currentUser._id &&
                !row.original?.isReadOnly
              }
              disableEditBtn={disableEditBtn}
              disableDeleteBtn={disableEditBtn}
              handleEdit={(row) =>
                handleEdit({
                  ...row.original,
                  isViewOnly: disableEditBtn,
                })
              }
              handleView={(row) =>
                handleEdit({
                  ...row.original,
                  isViewOnly: true,
                })
              }
              handleDelete={handleDelete}
            />
          </div>
        );
      },
    },
  ];

  const data = _orderBy(
    corporateShareholders,
    [
      "beneficialOwnershipForm.directPercentShareholding",
      "beneficialOwnershipForm.indirectPercentShareholding",
      "beneficialOwnershipForm.directPercentVotingRights",
      "beneficialOwnershipForm.indirectPercentVotingRights",
      "beneficialOwnershipForm.directRightRemoveDirector",
      "beneficialOwnershipForm.indirectRightRemoveDirector",
      "beneficialOwnershipForm.directCompanyControlRight",
      "beneficialOwnershipForm.indirectCompanyControlRight",
    ],
    ["desc", "desc", "desc", "desc", "desc", "desc", "desc", "desc"]
  );

  if (corporateShareholders.length > 10) {
    const minorityShareholders = data.splice(0, 1)[0];
    data.splice(10, 0, minorityShareholders);
  }

  if (corporateShareholders.length > 0) data.push({ id: "total" });

  return (
    <div className="w-full bg-white shadow">
      <div className="w-full overflow-x-auto">
        <ReactTable columns={columns} data={data} showSnColumn={false} />
      </div>
    </div>
  );
};

export default CorporateShareholderTable;
